import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Shield, Check, CreditCard, Loader2, Clock, Syringe, FileDown, Camera, Sparkles, CalendarDays, MessageCircle, ShoppingBag, Phone, Gift, Award, BookOpen, XCircle, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const subscriberFeatures = [
  { icon: Check, text: "Unlimited horses" },
  { icon: Syringe, text: "All 9 health & activity record trackers" },
  { icon: FileDown, text: "PDF export & download for all records" },
  { icon: Camera, text: "Secure cloud photo storage & gallery" },
  { icon: Sparkles, text: "AI-powered health insights" },
  { icon: CalendarDays, text: "Appointment calendar" },
  { icon: MessageCircle, text: "Community chat (1-on-1 & group)" },
  { icon: ShoppingBag, text: "Horse marketplace access" },
  { icon: Phone, text: "Emergency contacts directory" },
  { icon: Gift, text: "Referral program — earn $5 per friend" },
  { icon: Award, text: "Registration tracking (AQHA, APHA, etc.)" },
  { icon: BookOpen, text: "Breeding log & show records" },
];

export default function SubscriptionPage() {
  const { authHeaders, API, user, refreshUser, isTrialUser, trialDaysRemaining, hasFullAccess } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const sessionId = searchParams.get('session_id');
  const subscriptionPrice = user?.subscription_price || 9.95;
  const isEarlyBird = isTrialUser;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [sessionId]); // eslint-disable-line

  const pollPaymentStatus = async (sid, attempts) => {
    if (attempts >= 5) {
      setPaymentStatus('timeout');
      return;
    }
    try {
      const res = await axios.get(`${API}/payments/status/${sid}`, authHeaders());
      if (res.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        toast.success('Payment successful! Your subscription is now active.');
        refreshUser();
        return;
      } else if (res.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }
      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
    } catch (err) {
      setPaymentStatus('error');
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/payments/create-checkout`, { origin_url: origin }, authHeaders());
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error('Failed to initiate checkout');
      setLoading(false);
    }
  };

  const isActive = user?.subscription_status === 'active';
  const isCancelled = user?.subscription_status === 'cancelled';
  const isExpired = !hasFullAccess && !isTrialUser;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/subscription/cancel`, { reason: cancelReason }, authHeaders());
      toast.success('Subscription cancelled. Your records are safe.');
      setShowCancelDialog(false);
      setCancelReason('');
      refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-10 animate-fade-in">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332] mb-2" data-testid="subscription-title">
            Subscription
          </h1>
          <p className="text-[#4B6B58]">Manage your Stable Solutions subscription.</p>
        </div>

        {/* Status Messages */}
        {paymentStatus === 'paid' && (
          <div className="bg-[#E9EFE8] border border-[#2D6A4F]/20 rounded-[1.5rem] p-6 mb-8 text-center animate-fade-in" data-testid="payment-success">
            <Check size={32} className="text-[#2D6A4F] mx-auto mb-3" />
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-1">Payment Successful!</h3>
            <p className="text-[#4B6B58] text-sm">Your subscription is now active. Enjoy all features!</p>
          </div>
        )}
        {paymentStatus === 'processing' && (
          <div className="bg-[#F9F7F3] border border-[#D1E0D5] rounded-[1.5rem] p-6 mb-8 text-center" data-testid="payment-processing">
            <Loader2 size={32} className="text-[#4B6B58] mx-auto mb-3 animate-spin" />
            <p className="text-[#4B6B58]">Processing your payment...</p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Subscription Card */}
          <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(27,67,50,0.08)] animate-fade-in-delay-1" data-testid="subscription-card">
            <div className="w-14 h-14 rounded-full bg-[#1B4332]/10 flex items-center justify-center mx-auto mb-5">
              <Shield size={24} className="text-[#1B4332]" />
            </div>

            {isActive ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E9EFE8] text-[#2D6A4F] text-sm font-semibold mb-4">
                  <Check size={14} /> Active Subscription
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-1">Monthly Plan</h2>
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#1B4332]">
                    ${user?.subscription_tier === 'early_bird' ? '9.95' : '14.95'}
                  </span>
                  <span className="text-[#4B6B58] text-sm">/month</span>
                </div>
                {user?.subscription_tier === 'early_bird' && (
                  <p className="text-xs text-[#2D6A4F] font-medium bg-[#E9EFE8] px-3 py-1.5 rounded-full inline-block mb-3">Early bird rate locked for your first year</p>
                )}
                <p className="text-[#4B6B58] text-sm mb-6">You have full access to all features.</p>

                <button
                  onClick={() => setShowCancelDialog(true)}
                  data-testid="cancel-subscription-button"
                  className="text-sm text-[#4B6B58] hover:text-red-500 transition-colors underline underline-offset-2"
                >
                  Cancel subscription
                </button>
              </div>
            ) : isCancelled ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-sm font-semibold mb-4" data-testid="cancelled-badge">
                  <XCircle size={14} /> Subscription Cancelled
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">We Miss You!</h2>
                <p className="text-[#4B6B58] text-sm mb-2">Your records are safe and waiting for you.</p>
                <p className="text-[#4B6B58] text-sm mb-6">Re-subscribe anytime to regain full access.</p>

                <div className="bg-white border border-[#D1E0D5] rounded-[1.5rem] p-5 mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#1B4332]">$14.95</span>
                    <span className="text-[#4B6B58] text-sm">/month</span>
                  </div>
                  <p className="text-xs text-[#4B6B58] mt-1">Standard monthly rate</p>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="resubscribe-button"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
                  {loading ? 'Redirecting...' : 'Re-Subscribe — $14.95/mo'}
                </button>
                <p className="text-xs text-[#4B6B58] mt-3">Cancel anytime. No contracts.</p>
              </div>
            ) : isTrialUser ? (
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#E9EFE8] text-[#2D6A4F] text-sm font-semibold mb-4">
                  <Clock size={14} /> Free Month Active
                </div>
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-1">
                  {trialDaysRemaining} Day{trialDaysRemaining !== 1 ? 's' : ''} Left
                </h2>
                <p className="text-[#4B6B58] text-sm mb-4">Subscribe now to lock in the early bird rate!</p>
                <div className="w-full bg-[#E9EFE8] rounded-full h-2.5 mb-6">
                  <div className="bg-[#2D6A4F] h-2.5 rounded-full transition-all" style={{ width: `${(trialDaysRemaining / 30) * 100}%` }}></div>
                </div>

                {/* Early bird pricing highlight */}
                <div className="bg-[#E9EFE8] rounded-[1.5rem] p-5 mb-4">
                  <span className="inline-block px-3 py-0.5 rounded-full bg-[#1B4332] text-white text-[10px] font-bold uppercase tracking-wider mb-2">Early Bird Rate</span>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#2D6A4F]">$9.95</span>
                    <span className="text-[#4B6B58] text-sm">/month</span>
                  </div>
                  <p className="text-xs text-[#4B6B58] mt-1">Locked in for your entire first year</p>
                </div>
                <div className="bg-white border border-[#D1E0D5] rounded-xl p-3 mb-6">
                  <p className="text-xs text-[#4B6B58]">After 1st year: <span className="font-semibold text-[#122A1F]">$14.95</span>/month</p>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="subscribe-button"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {loading ? 'Redirecting...' : 'Subscribe — $9.95/mo'}
                </button>
                <p className="text-xs text-[#4B6B58] mt-3">Cancel anytime. No contracts.</p>
              </div>
            ) : (
              <div className="text-center">
                {isExpired && (
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-sm font-semibold mb-4" data-testid="trial-expired-badge">
                    <Clock size={14} /> Free Month Ended
                  </div>
                )}
                <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332] mb-2">
                  {isExpired ? "Don't Lose Your Records" : 'Monthly Plan'}
                </h2>
                {isExpired && (
                  <p className="text-[#92400E] text-sm mb-4 font-medium">Your horses' records are safe. Subscribe to manage them again.</p>
                )}
                <div className="bg-white border border-[#D1E0D5] rounded-[1.5rem] p-5 mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-['Cormorant_Garamond'] text-4xl font-bold text-[#1B4332]">$14.95</span>
                    <span className="text-[#4B6B58] text-sm">/month</span>
                  </div>
                  <p className="text-xs text-[#4B6B58] mt-1">Standard monthly rate</p>
                </div>

                <button
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="subscribe-button"
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />}
                  {loading ? 'Redirecting...' : 'Subscribe Now — $14.95/mo'}
                </button>
                <p className="text-xs text-[#4B6B58] mt-3">Cancel anytime. No contracts.</p>
              </div>
            )}
          </div>

          {/* Everything You Get */}
          <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgb(27,67,50,0.08)] animate-fade-in-delay-2" data-testid="subscriber-features-card">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-6 text-center">Everything You Get</h3>
            <div className="space-y-3">
              {subscriberFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F2F7F4] transition-colors" data-testid={`sub-feature-${i}`}>
                  <div className="w-7 h-7 rounded-md bg-[#E9EFE8] flex items-center justify-center flex-shrink-0">
                    <f.icon size={14} className="text-[#2D6A4F]" />
                  </div>
                  <span className="text-sm text-[#122A1F]">{f.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              Cancel Subscription?
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              Your records will be saved, but you'll lose access to editing, AI insights, chat, and other subscriber features.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-[#122A1F] mb-1">Reason for cancelling (optional)</label>
              <textarea
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                className="stable-input w-full"
                rows={3}
                placeholder="We'd love to know how we can improve..."
                data-testid="cancel-reason-input"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelDialog(false)}
                data-testid="keep-subscription-button"
                className="flex-1 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="confirm-cancel-button"
                className="flex-1 py-3 rounded-full border-2 border-red-300 text-red-500 font-semibold btn-lift hover:bg-red-50 disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
            <p className="text-xs text-[#4B6B58] text-center">You can re-subscribe at any time to regain access.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
